<script lang="ts">
    import type { Deal, Price } from '@shared/types/types';
    import { createBenefitTagState } from '@tile/components/benefit-tag/BenefitTagState.svelte.ts';
    import { parseGermanNumber, toCents } from './priceConversion.ts';

    interface Props {
        price: Price;
        deal?: Deal;
        variationId: string;
        rule?: string;
        showBenefitTag?: boolean;
        bestVariationBenefit?: string;
    }

    const { price, deal, variationId, rule, showBenefitTag = true, bestVariationBenefit }: Props = $props();

    const benefitTag = createBenefitTagState(() => variationId);
</script>

<ofc-pricing-item-v1
    retail-price={toCents(price.retailPrice)}
    old-price={toCents(price.sale ? price.comparativePrice : undefined)}
    suggested-retail-price={toCents(price.sale ? price.suggestedRetailPrice : undefined)}
    unit-price={toCents(price.normPrice?.value)}
    unit-price-amount={parseGermanNumber(price.normPrice?.amount)}
    unit-price-unit={price.normPrice?.unit}
    is-starting-price={price.isStartingPrice || undefined}
    deal-highlight={deal?.highlight}
    deal-id={deal?.dealId}
    display-alignment="left"
    display-size="100"
    meta-origin-feature="product-listing"
    meta-team="reptile"
>
    <!--
        Server-rendered price information for crawlers (documented `seo` slot of
        ofc-pricing-item-v1); mirrors the texts the status-quo price rendering shows.
    -->
    <div slot="seo" class="reptile-pricing-item__seo">
        {#if deal?.highlight}
            <span>{deal.highlight}</span>
        {/if}
        <span>{price.isStartingPrice ? 'ab ' : ''}{price.retailPrice} €</span>
        {#if price.sale && price.suggestedRetailPrice}
            <span>UVP {price.suggestedRetailPrice} €</span>
        {:else if price.sale && price.comparativePrice}
            <span>{price.comparativePrice} €</span>
        {/if}
        {#if price.normPrice}
            <span>({price.normPrice.value} €/ {price.normPrice.amount} {price.normPrice.unit})</span>
        {/if}
    </div>
    <!--
        Keep the ft9-benefit-tag-v1 attributes in sync with BenefitTag.svelte.
        The tag must be a direct slotted child (see ofc-pricing-item-v1 docs); Svelte cannot
        assign the slot through a component wrapper, and an always-rendered wrapper div would
        count as assigned slot content (phantom spacing) even when no benefit is shown.
    -->
    {#if showBenefitTag && (benefitTag.useVariationIdBenefit || bestVariationBenefit)}
        {#key variationId}
            <ft9-benefit-tag-v1
                slot="benefit-tag"
                data-secondary-action
                id={`${variationId}-benefit-tag`}
                current-context-dreson={rule}
                team="reptile"
                feature="tilelist"
                variation-id={benefitTag.useVariationIdBenefit ? variationId : undefined}
                data={benefitTag.useVariationIdBenefit ? undefined : bestVariationBenefit}
            ></ft9-benefit-tag-v1>
        {/key}
    {/if}
</ofc-pricing-item-v1>

<style lang="scss">
    .reptile-pricing-item {
        &__seo {
            /*                                                                      */
            /*                                                                 */
            display: none;
        }
    }
</style>
